import { z } from 'zod';

import i18n from '@/lib/i18n';
import { createPostalCodeRefinement, mainstreamCountrySchema } from '@/services/api/mainstream';
import { type InstitutionDetailsCompany as InstitutionDetailsCompanyApi, institutionDetailsCompanySchema, institutionDetailsRegisteredAddressSchema } from '@/services/api/profile';

const { refinement: postalCodeRefinement, params: postalCodeRefinementParams } = createPostalCodeRefinement<Partial<Pick<InstitutionCompany, 'country' | 'postCode'>>>({
  getParts: (data) => ({ country: data?.country, postCode: data?.postCode }),
  message: i18n.t('institutionDetails.company.errors.registeredAddressPostCode'),
  path: ['postCode'],
});

export const institutionCompanySchema = institutionDetailsCompanySchema
  .omit({ leiNumber: true, registeredAddress: true })
  .extend({
    leiNumber: z
      .union([institutionDetailsCompanySchema.shape.leiNumber, z.literal('')], {
        error: i18n.t('institutionDetails.company.errors.leiNumberInvalid'),
      })
      .transform((value) => (value === '' ? undefined : value)),
    address: z.string({ error: i18n.t('institutionDetails.company.errors.registeredAddress') }).min(1, i18n.t('institutionDetails.company.errors.registeredAddress')),
    address2: z.string().optional(),
    postCode: institutionDetailsRegisteredAddressSchema.shape.postalCode,
    city: institutionDetailsRegisteredAddressSchema.shape.locality,
    country: mainstreamCountrySchema(i18n.t('institutionDetails.company.errors.registeredAddressCountry')),
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type InstitutionCompany = z.infer<typeof institutionCompanySchema>;

export type InstitutionCompanyOrDefaults = InstitutionCompany | Partial<InstitutionCompany>;

export const institutionCompanyToInstitutionDetailsCompanyApi = ({ address, address2, postCode, city, country, ...company }: InstitutionCompany): InstitutionDetailsCompanyApi => ({
  ...company,
  registeredAddress: {
    addressLines: [address, ...(address2 ? [address2] : [])].map((line) => line.replace(/,/g, '')),
    country,
    locality: city,
    postalCode: postCode,
  },
});

export const institutionDetailsCompanyApiToInstitutionCompany = ({ registeredAddress, ...company }: InstitutionDetailsCompanyApi): InstitutionCompany => {
  const address = typeof registeredAddress === 'string' ? undefined : registeredAddress;

  return {
    ...company,
    leiNumber: company.leiNumber,
    address: address?.addressLines[0] ?? '',
    address2: address?.addressLines[1] ?? '',
    postCode: address?.postalCode ?? '',
    city: address?.locality ?? '',
    country: address?.country ?? '',
  };
};
